import React, { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import { formatDate } from "@fullcalendar/core";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";
import Select from "react-select";

// -- Event Edit Modal

function EventEditModal({
  isOpen,
  toggle,
  event,
  equipmentOptions,
  onSave,
  onDelete,
}) {
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [materials, setMaterials] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalError, setModalError] = useState("");

  // Seed form state whenever the selected event changes.
  useEffect(() => {
    if (!event) return;
    setModalError("");

    const ep = event.extendedProps || {};
    const ids = ep.equipmentIds || [];
    const names = ep.equipmentNames || [];

    setSelectedEquipment(
      ids.map((id, i) => ({ value: id, label: names[i] || String(id) }))
    );

    // Seed with the event-level override if set;
    setMaterials(ep.materials || "");
  }, [event]);

  if (!event) return null;

  const ep = event.extendedProps || {};
  const title = event.title || "Untitled Event";
  const customer = ep.customerName || "";
  const schedulingNotes = ep.schedulingNotes || "";
  const materialRequirements = ep.materialRequirements || "abc";

  const handleSave = async () => {
    setSaving(true);
    setModalError("");
    const result = await onSave(event.id, {
      equipmentIds: selectedEquipment.map((e) => e.value),
      equipmentNames: selectedEquipment.map((e) => e.label),
      materials,
    });
    setSaving(false);
    if (result?.ok === false) {
      setModalError(result.message || "Failed to save changes.");
    } else {
      toggle();
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setModalError("");
    const result = await onDelete(event.id);
    setDeleting(false);
    if (result?.ok === false) {
      setModalError(result.message || "Failed to remove event.");
    } else {
      toggle();
    }
  };

  const selectStyles = {
    dropdownIndicator: (base) => ({
      ...base,
      color: "#272727ff",
      "&:hover": { color: "#272727ff" },
    }),
  };

  const busy = saving || deleting;

  return (
    <Modal isOpen={isOpen} toggle={busy ? undefined : toggle} size="lg">
      <ModalHeader toggle={busy ? undefined : toggle}>
        <div>
          <span style={{ fontWeight: 700 }}>{title}</span>
          {customer && (
            <span
              style={{
                fontWeight: 400,
                fontSize: "0.9rem",
                color: "#555",
                marginLeft: "0.5rem",
              }}
            >
              — {customer}
            </span>
          )}
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Inline modal error */}
        {modalError && (
          <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
            {modalError}
          </div>
        )}

        {/* Scheduling Notes (read-only reference) */}
        {schedulingNotes && (
          <div
            className="alert alert-info py-2 px-3 mb-3"
            style={{ fontSize: "0.85rem" }}
          >
            <strong>Scheduling Notes:</strong> {schedulingNotes}
          </div>
        )}

        {/* Equipment */}
        <FormGroup>
          <Label for="eventEquipment">
            <strong>Equipment for this event</strong>
            <span
              style={{
                fontWeight: 400,
                fontSize: "0.8rem",
                color: "#888",
                marginLeft: "0.4rem",
              }}
            >
              (overrides job defaults)
            </span>
          </Label>
          <Select
            inputId="eventEquipment"
            isMulti
            isDisabled={busy}
            placeholder="Select equipment..."
            options={equipmentOptions}
            value={selectedEquipment}
            onChange={(vals) => setSelectedEquipment(vals || [])}
            styles={selectStyles}
          />
        </FormGroup>

        {/* Materials */}
        <FormGroup>
          <Label for="eventMaterials">
            <strong>Materials to bring on-site</strong>
            <span
              style={{
                fontWeight: 400,
                fontSize: "0.8rem",
                color: "#888",
                marginLeft: "0.4rem",
              }}
            >
              (specific to this event)
            </span>
          </Label>
          <Input
            id="eventMaterials"
            type="textarea"
            rows={4}
            disabled={busy}
            placeholder="List exactly what the crew needs to bring..."
            value={materials}
            onChange={(e) => setMaterials(e.target.value)}
          />
          {materialRequirements && (
            <div style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.3rem" }}>
              <strong>Job-level requirement (reference):</strong>{" "}
              {materialRequirements}
            </div>
          )}
        </FormGroup>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <Button color="danger" outline onClick={handleDelete} disabled={busy}>
          {deleting ? <Spinner size="sm" /> : "Remove Event"}
        </Button>
        <div className="d-flex gap-2">
          <Button color="secondary" outline onClick={toggle} disabled={busy}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSave} disabled={busy}>
            {saving ? <Spinner size="sm" /> : "Save Changes"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

// -- Calendar

export default function Calendar({
  events,
  setEvents,
  equipmentOptions,
  onVisibleRangeChange,
  onCreate,
  onUpdate,
  onDelete,
  jobContainerRef,
  jobCount,
  onError
}) {
  const calendarRef = useRef(null);
  const draggableRef = useRef(null); // holds the FullCalendar Draggable instance
  const [title, setTitle] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const syncTitle = () => {
    const api = calendarRef.current?.getApi();
    if (api) setTitle(api.view.title);
  };

  // Notify parent of the initial visible date range so it can load events.
  useEffect(() => {
    syncTitle();
    const api = calendarRef.current?.getApi();
    if (api && onVisibleRangeChange) {
      onVisibleRangeChange({
        start: new Date(api.view.currentStart),
        end: new Date(api.view.currentEnd),
      });
    }
  }, []);

  // -- Custom nav

  const navigate = (action) => {
    const api = calendarRef.current.getApi();
    api[action]();
    syncTitle();
    if (onVisibleRangeChange) {
      onVisibleRangeChange({
        start: new Date(api.view.currentStart),
        end: new Date(api.view.currentEnd),
      });
    }
  };

  const handlePrev  = () => navigate("prev");
  const handleNext  = () => navigate("next");
  const handleToday = () => navigate("today");

  // -- External drag init

  useEffect(() => {
    const containerEl = jobContainerRef?.current;
    if (!containerEl) return;

    // Destroy previous instance if it exists
    if (draggableRef.current) {
      draggableRef.current.destroy();
      draggableRef.current = null;
    }

    draggableRef.current = new Draggable(containerEl, {
      itemSelector: ".fc-external-job",
      eventData: (eventEl) => ({
        title: eventEl.getAttribute("data-name"),
        extendedProps: {
          jobId: Number(eventEl.getAttribute("data-id")),
          customerName: eventEl.getAttribute("data-customer") || "",
          equipmentIds: JSON.parse(eventEl.getAttribute("data-equipment-ids") || "[]"),
          equipmentNames: JSON.parse(eventEl.getAttribute("data-equipment-names") || "[]"),
          materialRequirements: eventEl.getAttribute("data-material-requirements") || "",
          schedulingNotes: eventEl.getAttribute("data-scheduling-notes") || "",
          materials: "",
        },
      }),
    });

    // Cleanup on unmount or before next run
    return () => {
      if (draggableRef.current) {
        draggableRef.current.destroy();
        draggableRef.current = null;
      }
    };
  }, [jobContainerRef, jobCount]);

  // -- Conflict detection

  const getDayKey = (d) => {
    if (!d) return null;
    const dateObj = d instanceof Date ? d : new Date(d);
    if (isNaN(dateObj.getTime())) return null;
    return formatDate(dateObj, {
      timeZone: "local",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const hasConflict = (event, allEvents) => {
    const eventDay = getDayKey(event.start);
    const eventEquip = event.extendedProps?.equipmentIds || [];
    if (!eventDay || eventEquip.length === 0) return false;

    return allEvents.some((e) => {
      if (e.id === event.id) return false;
      if (getDayKey(e.start) !== eventDay) return false;
      const otherEquip = e.extendedProps?.equipmentIds || [];
      return otherEquip.some((eq) => eventEquip.includes(eq));
    });
  };

  // -- FullCalendar event handlers

  // Job dragged from sidebar onto the calendar.
  const handleEventReceive = async (info) => {
    const plain = info.event.toPlainObject();
    const tempId = `temp-${Date.now()}`;

    // Add a temp event immediately with a neutral color for snappy UX.
    // onCreate returns { ok, color } - once we have the crew color we apply
    // it to the temp event. The parent then swaps it for the real saved event.
    const tempEvent = {
      ...plain,
      id: tempId,
      start: info.event.start,
      end: info.event.end || null,
      extendedProps: { ...(plain.extendedProps || {}) },
    };

    setEvents((prev) => [...prev, tempEvent]);

    const result = await onCreate(tempEvent);
    if (!result?.ok) {
      // Roll back on failure.
      setEvents((prev) => prev.filter((e) => e.id !== tempId));
    } else if (result.color) {
      // Apply the crew color to the temp event while the server response arrives to replace it
      setEvents((prev) =>
        prev.map((e) =>
          e.id === tempId
            ? { ...e, backgroundColor: result.color, borderColor: result.color }
            : e
        )
      );
    }
  };

  // Event dragged to a new time slot or resized.
  const handleEventChange = async (info) => {
    const result = await onUpdate(info.event.id, {
      start: info.event.start,
      end: info.event.end,
    });

    if (!result?.ok) {
      // Revert the drag/resize in the calendar UI.
      info.revert();
      if (onError) onError(result.message || "Failed to update event.");
    }
  };

  // Event clicked — open the edit modal.
  const handleEventClick = (info) => {
    const plain = info.event.toPlainObject();
    setSelectedEvent({
      id: info.event.id,
      title: info.event.title,
      extendedProps: plain.extendedProps || {},
    });
    setModalOpen(true);
  };

  // Modal save — equipment/materials changed.
  const handleModalSave = async (eventId, { equipmentIds, equipmentNames, materials }) => {
    return await onUpdate(eventId, { equipmentIds, equipmentNames, materials });
  };

  // Modal delete.
  const handleModalDelete = async (eventId) => {
    return await onDelete(eventId);
  };

  return (
    <>
      {/* Navigation row */}
      <Row
        className="align-items-center"
        style={{ height: "2.5rem", marginBottom: "1rem" }}
      >
        <Col className="d-flex gap-2 align-items-center text-center" xs="4">
          <div className="gap-0">
            <Button
              color="primary"
              className="rounded-end-0"
              size="md"
              onClick={handlePrev}
            >
              <BsChevronLeft />
            </Button>
            <Button
              color="primary"
              className="rounded-start-0"
              size="md"
              onClick={handleNext}
            >
              <BsChevronRight />
            </Button>
          </div>
          <Button size="md" onClick={handleToday} style={{ marginLeft: "0.5rem" }}>
            Today
          </Button>
        </Col>

        <Col xs="4">
          <h3 className="text-center">{title}</h3>
        </Col>

        <Col xs="4" />
      </Row>

      {/* Calendar */}
      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
        initialView="timeGridWeek"
        headerToolbar={false}
        height="80vh"
        slotMinTime="06:00:00"
        slotMaxTime="26:00:00"
        slotLabelInterval="02:00"
        defaultTimedEventDuration="02:00:00"
        editable
        droppable
        allDaySlot={false}
        hiddenDays={[0]}
        eventReceive={handleEventReceive}
        eventChange={handleEventChange}
        eventClick={handleEventClick}
        events={events}
        eventContent={(arg) => {
          const conflict = hasConflict(arg.event, events);
          const equipmentNames = arg.event.extendedProps?.equipmentNames || [];
          const crewName = arg.event.extendedProps?.crewName || "";

          return (
            <div style={{ padding: "2px 4px" }}>
              <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                {arg.event.title}
              </div>
              {crewName && (
                <div style={{ fontSize: "0.72rem", opacity: 0.85, marginBottom: "1px" }}>
                  {crewName}
                </div>
              )}
              <div style={{ fontSize: "0.75rem" }}>
                {equipmentNames.map((name) => (
                  <div key={name}>
                    <span style={{ color: conflict ? "var(--bs-red)" : undefined }}>
                      {name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        }}
      />

      {/* Event Edit Modal */}
      <EventEditModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        event={selectedEvent}
        equipmentOptions={equipmentOptions}
        onSave={handleModalSave}
        onDelete={handleModalDelete}
      />
    </>
  );
}